import React, { Component } from 'react';
import parse from 'html-react-parser';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';

import LinkButton from 'components/TrainingContent/LinkButton';
import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';
import { appContentUrl, storageKeys } from 'config/settings.json';
import RetainContent from './RetainContent';
import Sidebar from './Sidebar';

const verify = async (fallbackPath) => {
  const user = await StorageHandler.load(storageKeys.user);
  if (!user) return { redirect: '/login' };

  let chapters = await StorageHandler.load(storageKeys.chapters);

  if (!chapters) {
    try {
      const appId = await DataHandler.matchAppId();
      const fetchedChapters = await APIHandler.getChapters(appId);
      if (fetchedChapters && fetchedChapters.app) {
        await StorageHandler.save(storageKeys.chapters, fetchedChapters);
        chapters = fetchedChapters;
      } else {
        return { redirect: fallbackPath };
      }
    } catch (err) {
      console.error(`Failed to fetch chapters:`, err);
      return { redirect: fallbackPath };
    }
  }

  return { value: true, chapters };
};

export default class RetainContentView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      appId: -1,
      lang: '',
      verified: false,
      chapters: null,
      pages: null,
      items: [],
      buttons: [],
      redirect: null,
      loading: true,
    };

    this.switchPage = this.switchPage.bind(this);
  }

  componentDidMount() {
    document.querySelector('html').addEventListener('keydown', this.switchPage);
    this.initList();
  }

  componentDidUpdate(prevProps) {
    const { chapterId, pageId } = this.props.match.params;
    if (prevProps.match.params.chapterId !== chapterId) {
      this.resetAndLoadNewChapter(chapterId);
      return;
    }
    if (prevProps.match.params.pageId !== pageId) {
      this.updateActiveAndButtons(+pageId);
    }
  }

  componentWillUnmount() {
    document.querySelector('html').removeEventListener('keydown', this.switchPage);
  }

  async resetAndLoadNewChapter(chapterId) {
    this.setState({ pages: null, verified: false, loading: true });
    await this.setList(chapterId);
    this.setState({ loading: false });
  }

  async getPages(chapterId, lang = this.state.lang) {
    return APIHandler.getTrainingPages(chapterId, lang);
  }

  async setList(forcedChapterId = null, lang = this.state.lang) {
    const { chapterId, pageId } = this.props.match.params;
    const activeChapterId = forcedChapterId || chapterId;
    const search = this.getNavigationSearch();

    const pages = await this.getPages(activeChapterId, lang);
    const items = pages
      .map((page, i) => {
        return {
          name: page.title || page.pageType,
          type: page.pageType,
          active: i === +pageId,
          index: i,
          onClick: () => {
            const newUrl = `/main/ret/chapter/${activeChapterId}/${i}${search}`;
            if (newUrl !== this.props.match.url) {
              this.setState({
                verified: false,
                redirect: newUrl,
              });
            }
          },
        };
      })
      .filter(({ type }) => {
        return type !== undefined && type !== 'Final-Page';
      });

    this.setButtons(pages[+pageId]);
    this.setState({
      verified: true,
      items,
      pages,
      loading: false,
    });
  }

  updateActiveAndButtons(pageIndex) {
    this.setState((prev) => {
      const items = Array.isArray(prev.items)
        ? prev.items.map((it) => {
            return { ...it, active: it.index === pageIndex };
          })
        : prev.items;
      const pages = prev.pages || [];
      const buttons = [];
      const page = pages[pageIndex];
      if (page) {
        let index = 1;
        while (page[`link${index}`] !== undefined) {
          const hasValidLink =
            page[`link${index}`]?.length > 0 && page[`linkType${index}`]?.length > 0 && page.question === undefined;
          if (hasValidLink) {
            buttons.push(
              <LinkButton
                key={page[`linkType${index}`]}
                link={page[`linkType${index}`] === 'slideshow' ? page[`link_slideshow_${index}`] : page[`link${index}`]}
                type={page[`linkType${index}`]}
                shareTitle={page[`linkTitle${index}`] || ''}
                index={index}
                trainingCompleted={true}
              />,
            );
          }
          index++;
        }
      }
      return { items, buttons };
    });
  }

  setButtons(page) {
    const buttons = [];
    if (!page) {
      this.setState({ buttons });
      return;
    }

    let index = 1;
    while (page[`link${index}`] !== undefined) {
      const hasValidLink =
        page[`link${index}`]?.length > 0 && page[`linkType${index}`]?.length > 0 && page.question === undefined;

      if (hasValidLink) {
        buttons.push(
          <LinkButton
            key={page[`linkType${index}`]}
            link={page[`linkType${index}`] === 'slideshow' ? page[`link_slideshow_${index}`] : page[`link${index}`]}
            type={page[`linkType${index}`]}
            shareTitle={page[`linkTitle${index}`] || ''}
            index={index}
            trainingCompleted={true}
          />,
        );
      }
      index++;
    }

    this.setState({ buttons });
  }

  async initList() {
    const { code } = await StorageHandler.load(storageKeys.lang);
    // let lang = `${language}-${language.toUpperCase()}`;
    // lang = lang.replace("pt-PT", "pt-BR");

    const appId = await DataHandler.matchAppId();
    const verified = await verify(this.getReturnPath());

    if (verified.redirect) {
      this.setState({ redirect: verified.redirect });
      return;
    }

    const { value, chapters } = verified;

    this.setState({
      appId,
      verified: value || verified,
      chapters: chapters || null,
      lang: code,
    });

    await this.setList(null, code);
  }

  switchPage(e) {
    const { match } = this.props;
    const { pageId, chapterId } = match.params;
    const { items } = this.state;
    const search = this.getNavigationSearch();

    const i = items
      .map(({ index }) => {
        return index;
      })
      .indexOf(+pageId);
    let redirect = null;

    if (e.key === 'ArrowDown' && i < items.length - 1) {
      e.preventDefault();
      redirect = items[i + 1].index;
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      redirect = items[0].index;
    }
    if (e.key === 'ArrowUp' && i > 0) {
      e.preventDefault();
      redirect = items[i - 1].index;
    } else if (e.key === 'ArrowUp' && i === 0) {
      e.preventDefault();
      redirect = items[items.length - 1].index;
    }

    if (redirect !== null) {
      const newUrl = `/main/ret/chapter/${chapterId}/${redirect}${search}`;
      if (newUrl !== this.props.match.url) {
        this.setState({
          verified: false,
          redirect: newUrl,
        });
      }
    }
  }

  verifyUrl() {
    const { params } = this.props.match;
    const { pageId, chapterId } = params;
    const { chapters, pages } = this.state;
    if (chapters && pages) {
      return chapters.app.some((chapter) => {
        return +chapter.chapterID === +chapterId;
      }) &&
        +pageId >= 0 &&
        +pageId < pages.length ? null : (
        <Redirect to={this.getReturnPath()} />
      );
    }
    return null;
  }

  getReturnPath() {
    const params = new URLSearchParams(this.getNavigationSearch());
    return params.get('customRedir') === 'training' ? '/main/training' : '/main/ret';
  }

  getNavigationSearch() {
    const { location } = this.props;
    return location?.search || '';
  }

  render() {
    const {
      url,
      params: { chapterId, pageId },
    } = this.props.match;
    const { appId, verified, items, pages, chapters, buttons, redirect, loading } = this.state;

    if (loading) return <div className="retain container"></div>;

    if (redirect && redirect !== url) {
      const target = redirect;
      this.setState({ redirect: null });
      return <Redirect to={target} />;
    }

    return (
      <section className="retain container">
        {verified !== true ? verified : null}
        {items && chapters && (
          <Sidebar
            appId={Number(appId)}
            items={items}
            image={
              chapters.app.filter((chapter) => {
                return +chapter.chapterID === +chapterId;
              })?.[0]?.chapterImage
            }
          />
        )}
        {Array.isArray(pages) && pages.length > 0 && +pageId >= 0 && +pageId < pages.length && (
          <RetainContent
            appId={Number(appId)}
            image={`${appContentUrl}${appId}/${pages[+pageId].image}`}
            summary={{
              title: pages[+pageId].title || '',
              text: parse(pages[+pageId].summary || ''),
            }}
            params={pages[+pageId]}
            buttons={buttons}
          />
        )}
        {this.verifyUrl()}
      </section>
    );
  }
}

RetainContentView.propTypes = {
  match: PropTypes.shape().isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};
